// RITA-1547 — „Entwurf speichern“ auf der Bearbeiten-Seite: Anschreiben und Vertrag zusammen (Ticket),
// nie still überschreibend (Entscheidung 5, Bild G). Der Server vergleicht die geöffnete Revision; bei
// CONFLICT nennt er Name, Zeit und Revision der fremden Speicherung, bei DISCARDED wurde der Entwurf
// inzwischen verworfen. „Trotzdem speichern“ schickt die Revision aus der Warnung (nach DISCARDED keine) und
// ersetzt damit nur den genannten Stand. Als geändert gilt nur, was vom Stand beim Öffnen bzw. letzten
// Speichern abweicht – ein neu begonnener, unveränderter Entwurf fragt beim Verlassen nicht nach.
// RITA-1552: Editoren und Vergleichstexte gelten je enthaltener Art (Partial), damit die Art Anschreiben
// Rücksendung hinzukommen kann; der Entwurf „Vertrag erstellen“ umfasst weiter Anschreiben Versand und Vertrag.
// RITA-1552 (Skizze Rücklauf, Bild E; F8): Der Hook speichert den Entwurf eines Vorgangs – `vorgang` wählt die Mutation
// (Vertrag: saveVertragsentwurf wie bisher; Rücksendung: saveRuecksendungsentwurf mit `process: "return"`, eigene
// Revision), `arten` die Dokumente, die dazugehören. Vorgabe ist der Vertrag (3b/3c unverändert).
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useMutation } from '@apollo/client';
import type { ContractCreationDraftDocumentInput, ContractCreationKind } from 'global-intern-types';
import type { VertragEditorApi } from '../../templates/contractEditor/editor/useVertragEditor';
import {
  DOKUMENT_MAX_BYTES,
  hatSichtbarenText,
  speicherdokument,
  speicherText,
  utf8Bytes,
} from '../../templates/contractEditor/model/speicherform';
import { graphqlFehlerCode, graphqlFehlerErweiterungen } from '../../templates/services/vertragsvorlageFehler';
import { ENTWURFSDOKUMENTE, EntwurfSpeichernAntwort, EntwurfSpeichernVariablen } from '../api/vertragsentwurf';
import { BEWEGLICHE_PLATZHALTER } from '../services/entwurfsdokument';
import { verwendetePlatzhalter } from '../services/fehlendeAngaben';
import { alsArt } from '../services/vertragserstellungTexte';
import { leerText, PLATZHALTER_FEHLER, SPEICHERFEHLER, ZU_GROSS } from '../services/vertragsentwurfTexte';
import { Vorgang, VORGANG_ARTEN } from '../services/vorgang';

/** Gleiche Grenze wie CONTRACT_CREATION_DRAFT_MAX_BYTES im Backend (beide Dokumente zusammen). */
export const ENTWURF_MAX_BYTES = 900 * 1024;

/**
 * Eigene Texte je Fehlercode: Die Prüfmeldungen des Servers sind für Vorlagen formuliert („Der Vertrag …“,
 * „Diese Vertragsvorlage …“) und passen nicht zum Anschreiben eines Entwurfs (Abschlussreview H5).
 */
function fehlertext(error: unknown): string {
  const code = graphqlFehlerCode(error);
  if (code === 'INVALID_CONTENT') {
    const { kind } = graphqlFehlerErweiterungen(error);
    return leerText(alsArt(kind) ?? 'contract');
  }
  if (code === 'DOCUMENT_TOO_LARGE') return ZU_GROSS;
  if (code === 'INVALID_PLACEHOLDER') return PLATZHALTER_FEHLER;
  return SPEICHERFEHLER;
}

export interface Herkunft {
  template_id: number;
  template_name: string;
  template_updated_at: string;
}

export interface Speicherstand {
  am: string;
  von: string | null;
}

export type Konflikt =
  | { art: 'gespeichert'; von: string | null; am: string; revision: string | null }
  | { art: 'verworfen' };

export interface EntwurfSpeichernOptionen {
  agentId: number;
  /**
   * RITA-1552: Vorgang des Entwurfs (Vorgabe Vertrag); bestimmt die Mutation. Bleibt für eine Sitzung gleich (die Seite
   * hat je Berater und Vorgang eine eigene Sitzung).
   */
  vorgang?: Vorgang;
  /** RITA-1552: Arten des Vorgangs in der Reihenfolge der PDF (Vorgabe: die des Vorgangs). */
  arten?: readonly ContractCreationKind[];
  /** Editor je enthaltener Art (zu jeder Art in `herkunft` gehört ein Editor). */
  editoren: Partial<Record<ContractCreationKind, VertragEditorApi>>;
  /** Herkunft der enthaltenen Dokumente; fehlt eine Art, gehört sie nicht zum Entwurf. */
  herkunft: Partial<Record<ContractCreationKind, Herkunft>>;
  advisorValues: Record<string, string> | null;
  /**
   * Revision, die ersetzt werden darf (geöffnet oder in der Rückfrage bestätigt); null = keine bestätigt.
   * Gibt es trotzdem einen Entwurf, warnt das Speichern (CONFLICT).
   */
  startRevision: string | null;
  /** Stand beim Öffnen, wenn er dem gespeicherten Entwurf entspricht. */
  startGespeichert: Speicherstand | null;
  /** Nach jedem erfolgreichen Speichern; die Seite entscheidet über die Adresse (ohne „neu“). */
  onGespeichert?: () => void;
}

/** Vergleichstext eines Editors; ohne Editor leer. */
const textVon = (editor: VertragEditorApi | undefined) =>
  editor
    ? speicherText(speicherdokument({ dokument: editor.zustand.dokument, komposition: editor.zustand.komposition }))
    : '';

export default function useEntwurfSpeichern({
  agentId,
  vorgang = 'contract',
  arten = VORGANG_ARTEN[vorgang],
  editoren,
  herkunft,
  advisorValues,
  startRevision,
  startGespeichert,
  onGespeichert,
}: EntwurfSpeichernOptionen) {
  const [speichereEntwurf] = useMutation<EntwurfSpeichernAntwort, EntwurfSpeichernVariablen>(
    ENTWURFSDOKUMENTE[vorgang].speichern,
  );
  const anschreiben = editoren.dispatch_letter;
  const vertrag = editoren.contract;
  const ruecksendung = editoren.return_letter;
  const anschreibenText = useMemo(
    () => textVon(anschreiben),
    [anschreiben?.zustand.dokument, anschreiben?.zustand.komposition],
  );
  const vertragText = useMemo(() => textVon(vertrag), [vertrag?.zustand.dokument, vertrag?.zustand.komposition]);
  const ruecksendungText = useMemo(
    () => textVon(ruecksendung),
    [ruecksendung?.zustand.dokument, ruecksendung?.zustand.komposition],
  );
  const enthalten = arten.filter((art) => herkunft[art]);
  const texteJeArt: Partial<Record<ContractCreationKind, string>> = {
    dispatch_letter: anschreibenText,
    contract: vertragText,
    return_letter: ruecksendungText,
  };
  const aktuell: Partial<Record<ContractCreationKind, string>> = Object.fromEntries(
    enthalten.map((art) => [art, texteJeArt[art] ?? '']),
  );
  const [vergleich, setVergleich] = useState(() => aktuell);
  const [revision, setRevision] = useState(startRevision);
  const [gespeichert, setGespeichert] = useState(startGespeichert);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState('');
  const [konflikt, setKonflikt] = useState<Konflikt | null>(null);
  const speichertRef = useRef(false);
  const aktivRef = useRef(true);
  useLayoutEffect(() => {
    aktivRef.current = true;
    return () => {
      aktivRef.current = false;
    };
  }, []);

  const geaendert = enthalten.some((art) => aktuell[art] !== vergleich[art]);

  /** Speichert mit der erwarteten Revision (Standard: die geöffnete bzw. zuletzt gespeicherte). */
  async function speichernMit(erwartet: string | null): Promise<boolean> {
    if (speichertRef.current || !aktivRef.current) return false;
    const dokumente = enthalten.map((art) => ({
      art,
      dokument: speicherdokument((editoren[art] as VertragEditorApi).zustand),
    }));
    const leer = dokumente.find(({ dokument }) => !hatSichtbarenText(dokument));
    // Eingefügter Text kann Platzhalter enthalten (etwa aus einer Vorlage kopiert); der Server lehnt sie ab.
    const fremderPlatzhalter = dokumente.some(({ dokument }) =>
      verwendetePlatzhalter(dokument).some((schluessel) => !BEWEGLICHE_PLATZHALTER.includes(schluessel)),
    );
    const texte = { ...aktuell };
    const input = {
      documents: dokumente.map(
        ({ art, dokument }): ContractCreationDraftDocumentInput => ({
          kind: art,
          ...(herkunft[art] as Herkunft),
          document: dokument as unknown as Record<string, unknown>,
        }),
      ),
      advisor_values: advisorValues,
    };
    let pruefFehler = '';
    if (leer) pruefFehler = leerText(leer.art);
    else if (fremderPlatzhalter) pruefFehler = PLATZHALTER_FEHLER;
    else if (enthalten.some((art) => utf8Bytes(texte[art] ?? '') > DOKUMENT_MAX_BYTES)) pruefFehler = ZU_GROSS;
    else if (utf8Bytes(JSON.stringify(input)) > ENTWURF_MAX_BYTES) pruefFehler = ZU_GROSS;
    setFehler(pruefFehler);
    if (pruefFehler) return false;

    speichertRef.current = true;
    setSpeichert(true);
    try {
      const antwort = (
        await speichereEntwurf({
          variables: { agent_id: agentId, input, expected_revision: erwartet },
        })
      ).data?.saveContractCreationDraft;
      if (!aktivRef.current) return false;
      if (!antwort || typeof antwort.revision !== 'string' || !antwort.revision) {
        throw new Error('Ungültige Speicherantwort');
      }
      unstable_batchedUpdates(() => {
        setVergleich(texte);
        setRevision(antwort.revision);
        setGespeichert({ am: antwort.updated_at, von: antwort.updated_by_name });
        setKonflikt(null);
        setSpeichert(false);
      });
      onGespeichert?.();
      return true;
    } catch (error) {
      if (!aktivRef.current) return false;
      const code = graphqlFehlerCode(error);
      unstable_batchedUpdates(() => {
        if (code === 'CONFLICT') {
          const { updated_at: am, updated_by_name: von, revision: fremd } = graphqlFehlerErweiterungen(error);
          setKonflikt({
            art: 'gespeichert',
            am: typeof am === 'string' ? am : new Date().toISOString(),
            von: typeof von === 'string' ? von : null,
            revision: typeof fremd === 'string' ? fremd : null,
          });
        } else if (code === 'DISCARDED') {
          setKonflikt({ art: 'verworfen' });
        } else {
          setFehler(fehlertext(error));
        }
        setSpeichert(false);
      });
      return false;
    } finally {
      speichertRef.current = false;
    }
  }

  return {
    geaendert,
    speichert,
    revision,
    gespeichert,
    fehler,
    fehlerSchliessen: () => setFehler(''),
    konflikt,
    /** Ohne Revision in der Warnung (der Server nennt sie immer) ließe sich kein bestimmter Stand ersetzen. */
    trotzdemMoeglich: !(konflikt?.art === 'gespeichert' && !konflikt.revision),
    konfliktSchliessen: () => setKonflikt(null),
    speichern: () => speichernMit(revision),
    /**
     * „Trotzdem speichern“: ersetzt genau den in der Warnung genannten Stand; nach „inzwischen verworfen“
     * entsteht ein neuer Entwurf. Speichert inzwischen noch jemand, kommt die Warnung erneut.
     */
    trotzdemSpeichern: () => {
      if (konflikt?.art === 'gespeichert' && !konflikt.revision) return Promise.resolve(false);
      return speichernMit(konflikt?.art === 'gespeichert' ? konflikt.revision : null);
    },
  };
}
